import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp, 
  Lightning, 
  Target, 
  Warning,
  CheckCircle,
  XCircle,
  ArrowRight,
  TrendUp,
  ChartBar,
  Notebook,
  Clock,
  Scales,
  Robot,
  PencilSimple,
  Question,
  CaretDown,
  User,
  Gear,
  Brain,
  MagnifyingGlass
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const ManualVsAutomatedJournal = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Manual vs Automated Trading Journal | Which Is Better?',
      description: 'Compare manual vs automated trading journals and discover which method helps traders improve performance faster and more consistently.',
      path: '/manual-vs-automated-trading-journal',
      lang: 'en',
      alternates: getArticleAlternates('/manual-vs-automated-trading-journal'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const comparisonData = [
    { aspect: "Time Required", manual: "5-15 min per trade", automated: "< 1 min per trade", winner: "automated" },
    { aspect: "Data Accuracy", manual: "Prone to human error", automated: "100% accurate capture", winner: "automated" },
    { aspect: "Consistency", manual: "Varies by mood/energy", automated: "Every trade logged", winner: "automated" },
    { aspect: "Scalability", manual: "Breaks at 20+ trades/week", automated: "Handles unlimited trades", winner: "automated" },
    { aspect: "Pattern Detection", manual: "Manual calculation required", automated: "Automatic insights", winner: "automated" },
    { aspect: "Setup Cost", manual: "Free (spreadsheets)", automated: "Free to paid tiers", winner: "manual" },
    { aspect: "Customization", manual: "Fully customizable", automated: "Template-based", winner: "manual" },
    { aspect: "Learning Curve", manual: "Low", automated: "Low to medium", winner: "tie" }
  ];

  const faqs = [
    {
      question: "Is manual journaling enough for trading?",
      answer: "Manual journaling can work for traders taking fewer than 10 trades per week who are disciplined about documentation. However, most traders find that manual methods break down as volume increases or during stressful market periods. The lack of automated analytics also means you may miss patterns that could improve your results."
    },
    {
      question: "Why do traders switch to automation?",
      answer: "Traders typically switch to automated journaling when they realize they're spending more time logging trades than analyzing them, when they notice gaps in their data during losing streaks, or when they want performance metrics (win rate, profit factor, R:R) calculated automatically instead of manually."
    },
    {
      question: "Are trading journals necessary?",
      answer: "While not strictly required, trading journals are used by virtually all consistently profitable traders. Without documentation, you're relying on memory and emotion to guide decisions—both of which are unreliable. A journal provides objective data to improve your edge over time."
    },
    {
      question: "Can I use both manual and automated methods?",
      answer: "Yes. Some traders use automated tools for data capture and metrics while adding manual notes for context and psychology tracking. This hybrid approach combines the efficiency of automation with the reflection benefits of manual journaling."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      <header className="max-w-4xl mx-auto px-6 pt-16 pb-12">
        <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
          <Scales size={16} weight="fill" />
          <span>Trading Journal Comparison</span>
        </div>
        
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
          Manual vs Automated Trading Journal
          <span className="block text-white/60 text-2xl md:text-3xl mt-2">
            Which One Actually Works?
          </span>
        </h1>
        
        <p className="text-lg text-white/70 leading-relaxed max-w-3xl">
          Most traders start with spreadsheets or notebooks. It feels simple, free, and under control. 
          But as trade volume grows and the data piles up, limitations appear. This <strong className="text-white">trading journal comparison</strong> breaks 
          down exactly when manual works, when it doesn't, and why serious traders eventually move to automation. 
          If you're trading crypto specifically, check out our guide to the <Link to="/crypto-trading-journal" className="text-[#00E676] hover:underline">best crypto trading journal</Link>.
        </p>
      </header>

      <main className="max-w-4xl mx-auto px-6 pb-20">
        
        {/* What Is Manual */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <PencilSimple size={28} weight="duotone" className="text-orange-400" />
            What Is a Manual Trading Journal?
          </h2>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg p-8">
            <p className="text-white/80 leading-relaxed mb-6">
              A <strong className="text-white">manual trading journal</strong> is any system where you 
              log trades by hand—whether in a spreadsheet, notebook, or basic document. You enter 
              every field yourself: entry price, exit price, position size, outcome, and notes.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Google Sheets or Excel spreadsheets",
                "Physical notebooks or planners",
                "Note-taking apps (Notion, Evernote)",
                "Basic templates downloaded online"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-white/60 text-sm">
                  <PencilSimple size={14} className="text-orange-400" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What Is Automated */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <Robot size={28} weight="duotone" className="text-[#00E676]" />
            What Is an Automated Trading Journal?
          </h2>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg p-8">
            <p className="text-white/80 leading-relaxed mb-6">
              An <strong className="text-white">automated trading journal</strong> is software that 
              captures trade data automatically or with minimal input. It calculates performance 
              metrics in real-time, identifies patterns, and provides actionable insights without 
              manual calculation.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Automatic trade logging from charts",
                "Real-time win rate and profit factor",
                "Setup categorization and filtering",
                "Performance trends and analytics"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-white/60 text-sm">
                  <Robot size={14} className="text-[#00E676]" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Differences - Comparison Table */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <ChartBar size={28} weight="duotone" className="text-purple-400" />
            Key Differences: Manual vs Automated
          </h2>
          
          <p className="text-white/70 mb-8 leading-relaxed">
            This <strong className="text-white">manual vs automated trading journal</strong> comparison 
            shows where each method excels and where it falls short.
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-[#121212]">
                  <th className="text-left py-4 px-4 text-white/60 font-medium border-b border-white/10">Aspect</th>
                  <th className="text-left py-4 px-4 text-orange-400 font-medium border-b border-white/10">
                    <span className="flex items-center gap-2">
                      <PencilSimple size={16} />
                      Manual
                    </span>
                  </th>
                  <th className="text-left py-4 px-4 text-[#00E676] font-medium border-b border-white/10">
                    <span className="flex items-center gap-2">
                      <Robot size={16} />
                      Automated
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-4 font-medium">{row.aspect}</td>
                    <td className={`py-4 px-4 ${row.winner === 'manual' ? 'text-orange-400' : 'text-white/60'}`}>
                      {row.winner === 'manual' && <CheckCircle size={14} weight="fill" className="inline mr-1" />}
                      {row.manual}
                    </td>
                    <td className={`py-4 px-4 ${row.winner === 'automated' ? 'text-[#00E676]' : 'text-white/60'}`}>
                      {row.winner === 'automated' && <CheckCircle size={14} weight="fill" className="inline mr-1" />}
                      {row.automated}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Pros and Cons - Manual */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            Pros and Cons of Manual Journaling
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#121212] border border-green-500/20 rounded-lg p-6">
              <h3 className="font-semibold mb-4 text-green-400 flex items-center gap-2">
                <CheckCircle size={18} weight="fill" />
                Pros
              </h3>
              <ul className="space-y-3">
                {[
                  "Free to use (spreadsheets)",
                  "Complete customization control",
                  "Forces active engagement with trades",
                  "No learning curve for basic use",
                  "Works offline without software"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-white/70 text-sm">
                    <CheckCircle size={14} weight="fill" className="text-green-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-[#121212] border border-red-500/20 rounded-lg p-6">
              <h3 className="font-semibold mb-4 text-red-400 flex items-center gap-2">
                <XCircle size={18} weight="fill" />
                Cons
              </h3>
              <ul className="space-y-3">
                {[
                  "Time-consuming data entry",
                  "Prone to errors and inconsistency",
                  "No automatic performance metrics",
                  "Difficult to spot patterns manually",
                  "Often abandoned after losing streaks"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-white/70 text-sm">
                    <XCircle size={14} weight="fill" className="text-red-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Pros and Cons - Automated */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            Pros and Cons of Automated Journaling
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#121212] border border-green-500/20 rounded-lg p-6">
              <h3 className="font-semibold mb-4 text-green-400 flex items-center gap-2">
                <CheckCircle size={18} weight="fill" />
                Pros
              </h3>
              <ul className="space-y-3">
                {[
                  "Fast and efficient logging",
                  "100% data accuracy",
                  "Real-time performance analytics",
                  "Automatic pattern recognition",
                  "Consistent tracking regardless of mood"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-white/70 text-sm">
                    <CheckCircle size={14} weight="fill" className="text-green-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-[#121212] border border-red-500/20 rounded-lg p-6">
              <h3 className="font-semibold mb-4 text-red-400 flex items-center gap-2">
                <XCircle size={18} weight="fill" />
                Cons
              </h3>
              <ul className="space-y-3">
                {[
                  "May require subscription for full features",
                  "Less customization than spreadsheets",
                  "Learning curve for new tools",
                  "Requires internet for cloud-based tools",
                  "Can feel less personal than manual notes"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-white/70 text-sm">
                    <XCircle size={14} weight="fill" className="text-red-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Real Scenario */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <User size={28} weight="duotone" className="text-blue-400" />
            Real Scenario: 50+ Trades Per Week
          </h2>
          
          <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 rounded-lg p-8">
            <p className="text-white/80 leading-relaxed mb-6">
              Consider a day trader taking <strong className="text-white">50 trades per week</strong>. 
              Here's how the two approaches compare in practice:
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold mb-3 text-orange-400 flex items-center gap-2">
                  <PencilSimple size={18} />
                  Manual Approach
                </h3>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li>• 50 trades × 10 min = 8+ hours/week logging</li>
                  <li>• Errors accumulate over high volume</li>
                  <li>• Calculating weekly stats: 30+ minutes</li>
                  <li>• Pattern analysis: nearly impossible</li>
                  <li>• After a bad week: often skipped entirely</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3 text-[#00E676] flex items-center gap-2">
                  <Robot size={18} />
                  Automated Approach
                </h3>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li>• 50 trades × 30 sec = ~25 minutes/week</li>
                  <li>• Zero data entry errors</li>
                  <li>• Stats calculated instantly</li>
                  <li>• Patterns surfaced automatically</li>
                  <li>• Every trade logged regardless of outcome</li>
                </ul>
              </div>
            </div>
            
            <p className="text-white/70 text-sm mt-6 pt-6 border-t border-white/10">
              <strong className="text-white">Result:</strong> The manual trader spends 8 hours on data entry 
              with incomplete insights. The automated trader spends 25 minutes and gets full analytics.
            </p>
          </div>
        </section>

        {/* When Manual Makes Sense */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <PencilSimple size={28} weight="duotone" className="text-orange-400" />
            When Manual Journaling Still Makes Sense
          </h2>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg p-8">
            <p className="text-white/70 leading-relaxed mb-6">
              Manual journaling isn't dead. It works well for specific situations:
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: "Complete Beginners", desc: "Learning the habit of documentation" },
                { title: "Very Low Frequency", desc: "Fewer than 5 trades per week" },
                { title: "Budget Constraints", desc: "No budget for premium tools" },
                { title: "Specific Customization", desc: "Unique tracking requirements" }
              ].map((item, index) => (
                <div key={index} className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-medium text-sm mb-1">{item.title}</h4>
                  <p className="text-white/50 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Traders Move to Automation */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <TrendUp size={28} weight="duotone" className="text-[#00E676]" />
            Why Most Serious Traders Move to Automation
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              As traders mature, their needs evolve. What starts as a simple spreadsheet becomes 
              insufficient when you need to answer questions like: "What's my win rate on breakout 
              trades in the first hour?" or "How does my performance differ between BTC and altcoins?" 
              Understanding <Link to="/how-to-find-a-trading-setup" className="text-[#00E676] hover:underline">how to find a profitable trading setup</Link> requires 
              this level of data granularity.
            </p>
            <p className="text-white/70 leading-relaxed">
              Tools like <strong className="text-white">GENI</strong> allow traders to automate their 
              journaling, reduce errors, and focus on improving performance instead of managing spreadsheets. 
              The time saved on data entry gets redirected to actual analysis and strategy refinement.
            </p>
            <p className="text-white/70 leading-relaxed">
              This <strong className="text-white">trading journal comparison</strong> consistently shows 
              that traders who switch to automation report better consistency, clearer insights, and 
              faster improvement in their results.
            </p>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            The Verdict
          </h2>
          
          <div className="bg-gradient-to-br from-[#00E676]/10 to-purple-500/10 border border-[#00E676]/30 rounded-lg p-8">
            <p className="text-white/80 leading-relaxed mb-4">
              In the <strong className="text-white">manual vs automated trading journal</strong> debate, 
              automation wins for most traders. The time savings, accuracy, and analytical power 
              simply can't be matched by spreadsheets at scale.
            </p>
            <p className="text-white/70 leading-relaxed">
              Manual journaling has its place for beginners and very low-frequency traders. But if 
              you're serious about improving—if you want to identify your edge, cut your leaks, 
              and track progress over time—an <strong className="text-white">automated trading journal</strong> is 
              the clear choice.
            </p>
          </div>
        </section>

        {/* Internal Links */}
        <section className="mb-16">
          <div className="p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm mb-3">
              <strong>Related Reading:</strong>
            </p>
            <div className="flex flex-col gap-2">
              <Link to="/automated-trading-journal" className="text-[#00E676] hover:underline text-sm">
                What Is an Automated Trading Journal? →
              </Link>
              <Link to="/crypto-trading-journal" className="text-[#00E676] hover:underline text-sm">
                Best Trading Journal for Crypto Traders →
              </Link>
              <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline text-sm">
                How to Analyze Your Trades Like a Pro →
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-16">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/how-to-find-a-trading-setup" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Find a Trading Setup</h3>
              <p className="text-white/50 text-sm">Discover what makes a profitable trading setup.</p>
            </Link>
            <Link to="/why-traders-fail-to-improve" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Why Traders Fail to Improve</h3>
              <p className="text-white/50 text-sm">Common mistakes that keep traders stuck in place.</p>
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Question size={28} weight="duotone" className="text-purple-400" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <CaretDown 
                    size={18} 
                    className={`text-white/40 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mb-8">
          <div className="bg-gradient-to-br from-purple-500/10 via-[#121212] to-[#00E676]/10 border border-purple-500/30 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <Lightning size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Switch to Automation?
            </h2>
            
            <p className="text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
              Switch to a more efficient way of tracking your trades and focus on what actually 
              improves your performance—not spreadsheet management.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Start Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "Manual vs Automated Trading Journal: Which One Actually Works?",
              "description": "Compare manual vs automated trading journals and discover which method helps traders improve performance faster and more consistently.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://genitradeapp.com/manual-vs-automated-trading-journal"
              }
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </main>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ManualVsAutomatedJournal;
